import React, { useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { DISCARD_CHANGES_PROMPT } from '../constants/unsavedChanges'
import { hasUnsavedChanges } from '../hooks/useUnsavedChanges'
import { useSpeedbump } from './Speedbump'

/**
 * Guards both ways out of an editor, reading the same registry for each:
 *
 * - `beforeunload` for tab close, reload and external links. Browsers ignore
 *   custom text on this event, so the user gets the generic dialog and we
 *   can't show a Speedbump here.
 * - one router blocker for in-app navigation, including the back button.
 *
 * One blocker for the whole app rather than one per form: they'd all be
 * competing to answer the same navigation.
 */
const UnsavedChangesGuard: React.FC = () => {
  const confirm = useSpeedbump()

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return
      e.preventDefault()
      // Some browsers still need returnValue set to show the dialog at all.
      e.returnValue = ''
    }
    // Registered unconditionally rather than on dirty transitions: keeping it
    // attached costs a disabled bfcache, which is worthless on an internal
    // tool, and buys a registry nothing has to subscribe to.
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // Compares pathname only. Drawers and filters that live in the query string
  // aren't departures, and the list pages rewrite search params constantly.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges() && currentLocation.pathname !== nextLocation.pathname,
  )

  // useBlocker returns a fresh object per state change; this keeps a second
  // render from stacking a second dialog on the first.
  const prompting = useRef(false)

  useEffect(() => {
    if (blocker.state !== 'blocked' || prompting.current) return
    prompting.current = true
    void (async () => {
      const discard = await confirm({ ...DISCARD_CHANGES_PROMPT })
      prompting.current = false
      if (discard) blocker.proceed?.()
      else blocker.reset?.()
    })()
  }, [blocker, confirm])

  return null
}

export default UnsavedChangesGuard
